import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {BoundingBox, Hotel} from '@findhotel/sapi'
import {getCurrencyCode, getMeta} from 'modules/meta/selectors'
import {getIsRtlLanguage} from 'modules/meta/selectors'
import {getBaseMappingContextFromState} from 'modules/sapiSearch/offersMapping'
import {
  getNumberOfRooms,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {getUseBoVioFirstProfile} from 'modules/search/selectors'
import {getHotelIdFromAccommodationPage} from 'utils/getHotelIdFromAccommodationPage'
import {imageProvider} from 'utils/imageProvider'

import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {getPersistedSapiLabel} from '@vio/core/src/_web/sapiLabel/business'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  HotelContext,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'
import {useGetSimilarHotels} from '@vio/core/src/carouselWidgets/useGetSimilarHotels'
import {getCashback, getHotelPrices} from '@vio/core/src/carouselWidgets/utils'
import {getNightlyPriceForAllRooms} from '@vio/core/src/price/business/price'
import {BOVIO_FIRST_PROFILE} from '@vio/core/src/profile/constants'
import {SearchState} from '@vio/core/src/sapi/services/searchApi'
import {
  SuggestionHotelCard,
  SuggestionHotelCardSkeleton
} from '@vio/shared/src/search/SuggestionHotelCard'

import {
  getIsMultiRoomBundlesSearch,
  getUrlParams
} from '../../../../modules/search/selectors'
import {useMetadataForItinerary} from '../../hooks/useMetadataForItinerary'

const SkeletonContainer = styled.div`
  display: flex;
  overflow: hidden;
  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
  > div + div {
    margin-left: ${cssTheme.layout.spacing.s500};
  }
  ${linariaMq.desktopXs} {
    padding-left: 0;
    padding-right: 0;
  }
`

interface SimilarHotelsProps {
  boundingBox?: BoundingBox
  hotelId?: string
  placeId?: string
  address?: string
  rooms: string
  checkIn: string
  checkOut: string
  trackingPayload: Pick<TrackEventPayload, 'component' | 'payload' | 'team'>
  onHotelClick: ({hotelId}) => void
  hasInsideMargin?: boolean
  onRequestComplete?: (hotelsIds: Array<string>) => void
  shouldWaitRequestComplete?: boolean
  hasSidePaddings?: boolean
  parentId?: string
  numberOfResults?: number
  includeSameProperty?: boolean
  isNoDatesSearch?: boolean
}

interface SimilarHotelItemProps
  extends Pick<
    SimilarHotelsProps,
    'checkIn' | 'checkOut' | 'trackingPayload' | 'hasInsideMargin'
  > {
  hotelEntity: Hotel
  offerEntity: HotelOfferEntity
  position: number
  onClick: ({hotelId}) => void
  isNoDatesSearch?: boolean
}

const SimilarHotelItem = ({
  hotelEntity,
  offerEntity,
  checkIn,
  checkOut,
  trackingPayload,
  hasInsideMargin,
  onClick,
  position,
  isNoDatesSearch
}: SimilarHotelItemProps) => {
  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const currencyCode = useSelector(getCurrencyCode)
  const dispatch = useDispatch()
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)
  const numberOfRooms = useSelector(state =>
    getNumberOfRooms(state, hotelEntity.objectID)
  )
  const {
    monetisation: {cashback: cashbackCapability}
  } = useBrandCapabilities()

  if (!hotelEntity) return null

  const {
    hotelName,
    reviewCount,
    starRating,
    imageURIs,
    guestRating,
    objectID,
    pricing
  } = hotelEntity

  const {price, anchorPrice} = getHotelPrices({
    offerEntity,
    checkIn,
    checkOut,
    includeTaxes,
    includeLocalTaxes
  })
  const {cashback} = getCashback(offerEntity)

  const calculatedNightlyPrice = getNightlyPriceForAllRooms(
    price,
    isMultiRoomBundlesSearch,
    numberOfRooms
  )
  const calculatedAnchorPrice = getNightlyPriceForAllRooms(
    anchorPrice,
    isMultiRoomBundlesSearch,
    numberOfRooms
  )

  const onHotelClick = () => {
    const hotelIdFromAP = getHotelIdFromAccommodationPage()

    const hotelContext = {
      hotelId: objectID,
      hotelPosition: position,
      ...(Boolean(hotelIdFromAP) && {
        sourceHotelId: hotelIdFromAP
      })
    } as HotelContext

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelCard,
        action: Action.Clicked,
        analyticsContext: {
          [AnalyticsContext.HotelContext]: hotelContext
        },
        ...trackingPayload
      })
    )

    onClick({hotelId: objectID})
  }

  return (
    <SuggestionHotelCard
      key={objectID}
      hotelName={hotelName}
      starRating={starRating}
      reviewRating={guestRating?.overall}
      reviewRatingCount={reviewCount}
      imageUrl={imageURIs?.[0]}
      currency={currencyCode}
      price={
        isNoDatesSearch
          ? pricing?.minRateInUserCurrency
          : calculatedNightlyPrice
      }
      anchorPrice={isNoDatesSearch ? undefined : calculatedAnchorPrice}
      onHotelClick={onHotelClick}
      imageProvider={imageProvider}
      hasInsideMargin={hasInsideMargin}
      cashback={cashbackCapability.enabled ? cashback : undefined}
    />
  )
}

const SuggestedHotelsSkeleton = () => (
  <SkeletonContainer>
    {[...Array(5)].map((_, index) => (
      <SuggestionHotelCardSkeleton key={index} />
    ))}
  </SkeletonContainer>
)

export const SimilarHotels = ({
  boundingBox,
  hotelId,
  placeId,
  address,
  rooms,
  checkIn,
  checkOut,
  trackingPayload,
  onHotelClick,
  hasInsideMargin,
  hasSidePaddings,
  onRequestComplete,
  shouldWaitRequestComplete,
  numberOfResults = 10,
  parentId,
  includeSameProperty = false,
  isNoDatesSearch = false
}: SimilarHotelsProps) => {
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const mappingContext = useSelector(getBaseMappingContextFromState)
  const {cugDeals, tier} = useSelector(getSearchParameters) || {}
  const metadata = useMetadataForItinerary(hotelId, checkIn, checkOut)
  const {profile} = useSelector(getUrlParams) || {}
  const sapiLabel = getPersistedSapiLabel()
  const boVioFirstProfileIsEnabled = useSelector(getUseBoVioFirstProfile)

  // wait until tier is defined by syncStoreWithUrl to avoid doing duplicate searches
  const shouldSkip = tier === undefined

  const {data, isLoading, error} = useGetSimilarHotels(
    {
      boundingBox,
      hotelId,
      placeId,
      address,
      checkIn,
      checkOut,
      rooms,
      tier,
      cugDeals,
      metadata,
      pageSize: numberOfResults,
      queryContext: trackingPayload,
      mappingContext,
      ...(sapiLabel && {label: sapiLabel}),
      ...(boVioFirstProfileIsEnabled && {
        originId: BOVIO_FIRST_PROFILE
      }),
      ...(profile && {originId: profile})
    },
    {
      shouldWaitRequestComplete,
      includeSameProperty,
      skip: shouldSkip
    }
  )

  useEffect(() => {
    if (data?.searchState === SearchState.Completed)
      onRequestComplete?.(data.hotelIds)
  }, [data])

  if (isLoading) {
    return <SuggestedHotelsSkeleton />
  }

  if (error) return null

  return (
    <SmoothScrollNavigation
      isRtlLanguage={isRtlLanguage}
      hasSidePaddings={hasSidePaddings}
    >
      {data.hotelIds.map((hotelId, index) => (
        <SimilarHotelItem
          key={`${parentId}-${hotelId}`}
          hotelEntity={data?.hotelEntities[hotelId]}
          offerEntity={data?.hotelOfferEntities[hotelId]}
          trackingPayload={{
            ...trackingPayload,
            payload: {
              totalNumber: data?.resultsCount,
              index: index + 1,
              searchItem: {
                hotelId,
                checkIn,
                checkOut,
                rooms
              }
            }
          }}
          checkIn={checkIn}
          checkOut={checkOut}
          position={index + 1}
          hasInsideMargin={hasInsideMargin}
          onClick={onHotelClick}
          isNoDatesSearch={isNoDatesSearch}
        />
      ))}
    </SmoothScrollNavigation>
  )
}
